"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const smartSurveyPushBlockInputs = [
  {
    name: "imgDesktop",
    friendlyName: "Image desktop",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    required: true,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F035813b7aeb845139d31942675bcbb18",
  },
  {
    name: "imgMobile",
    friendlyName: "Image mobile",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F66a0f35259e74956bd32ad83cc4490f5",
  },
  {
    name: "alt",
    friendlyName: "Alt Text",
    type: "string",
    defaultValue: "Push block image",
  },
  {
    name: "orientation",
    type: "string",
    enum: ["left-side-text", "right-side-text", "left-side-text-wide", "right-side-text-wide"],
    required: true,
    defaultValue: "left-side-text",
  },
  {
    name: "type",
    friendlyName: "Survey Type",
    type: "string",
    enum: ["one_time", "recurring"],
    required: true,
    defaultValue: "one_time",
  },
  {
    name: "dateOfUserRegistrationForSurvey",
    friendlyName: "Minimal date of user registration to see the survey",
    type: "date",
    required: true,
    helperText:
      "Minimal date of user registration to see the survey, it would not display for user registered before this date",
    defaultValue: "2025-06-01T00:00:00.000Z",
  },
  {
    name: "title",
    friendlyName: "Title",
    type: "string",
    required: true,
    defaultValue: "Welcome to the Inner Circle",
  },
  {
    name: "subtitle",
    friendlyName: "Sub header text for additional information",
    type: "string",
    required: true,
    defaultValue: "Instant 600 Points Just for Saying Hello!",
  },
  {
    name: "description",
    friendlyName: "Survey Description Text",
    type: "string",
    required: true,
    defaultValue: "Your unique code is your passport to exclusive rewards and a personalized shopping experience.",
  },
  {
    name: "preCtaText",
    friendlyName: "Text before CTA",
    required: true,
    type: "string",
    defaultValue: "Welcome code",
  },
  {
    name: "ctaText",
    friendlyName: "CTA",
    required: false,
    type: "string",
    defaultValue: "Where can I find it?",
  },
  {
    name: "ctaUrl",
    friendlyName: "CTA Url for redirect",
    required: false,
    type: "url",
  },
  {
    name: "inputPlaceholder",
    friendlyName: "Input Placeholder",
    type: "string",
    required: true,
    defaultValue: "Welcome Code",
  },
  {
    name: "buttonText",
    friendlyName: "Start Survey Button Text",
    type: "string",
    required: true,
    defaultValue: "Enter Survey",
  },
  {
    name: "surveyMonkeyPageUrl",
    friendlyName: "Hyperlink to survey page",
    required: true,
    type: "url",
  },
  {
    name: "titleAllCompletedRecurringSurveys",
    friendlyName: "Title to inform the user that all the surveys are completed",
    type: "string",
    defaultValue: "See you next time",
  },
  {
    name: "subtitleAllCompletedRecurringSurveys",
    friendlyName: "Days until next survey label - days left",
    type: "string",
    defaultValue: "days left",
  },
  {
    name: "descriptionAllCompletedRecurringSurveys",
    friendlyName: "Description to inform the user that all the surveys are completed",
    type: "string",
    defaultValue:
      "Hold tight! Our new survey is on the way. Watch the countdown to see when your next opportunity to earn points will arrive.",
  },
  {
    name: "ctaButtonAllCompletedRecurringSurveys",
    friendlyName: "Button text for CTA to get more points",
    type: "string",
    defaultValue: "Earn more points",
  },
  {
    name: "redirectUrlAllCompletedRecurringSurveys",
    friendlyName: "Hyperlink for get more points CTA",
    type: "url",
    defaultValue: "",
  },
  {
    name: "isDeviceRegistrationRequired",
    type: "boolean",
    friendlyName: "Is device registration required?",
    defaultValue: false,
  },
  {
    name: "is12Characters",
    type: "boolean",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    friendlyName: "Is code length 12 characters?",
    defaultValue: false,
  },
  {
    name: "is15Characters",
    type: "boolean",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    friendlyName: "Is code length 15 characters?",
    defaultValue: false,
  },
  {
    name: "is16Characters",
    type: "boolean",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    friendlyName: "Is code length 16 characters?",
    defaultValue: true,
  },
  {
    name: "theme",
    friendlyName: "Device registration dialog theme",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    enum: ["light", "dark"],
    defaultValue: "light",
  },
  {
    name: "registerDeviceImgDesktop",
    friendlyName: "Image desktop for device registration dialog",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F035813b7aeb845139d31942675bcbb18",
  },
  {
    name: "registerDeviceImgMobile",
    friendlyName: "Image mobile for device registration dialog",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    defaultValue:
      "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F66a0f35259e74956bd32ad83cc4490f5",
  },
  {
    name: "registerDeviceImgAlt",
    friendlyName: "Alt Text for image in device registration dialog",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    defaultValue: "alt",
  },

  {
    name: "missingDeviceRegistrationDescription",
    friendlyName: "Additional text for SmartSurvey block with device registration information",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    defaultValue:
      "You haven't registered your device yet, so you'll be asked to do so before starting the survey. You'll earn an extra XXX points for registering.",
  },
  {
    name: "registerDeviceDialogTitle",
    friendlyName: "Title for the device registration dialog",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    defaultValue: "Register Your Device First and Earn XXX Extra Points",
  },
  {
    name: "registerDeviceDialogDescription",
    friendlyName: "Description for the device registration dialog",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    defaultValue:
      "Before you start the survey, you have to register your device. It only takes a few moments and you'll open up a world of benefits like [LOCAL MARKET LIST OF REGISTRATION BENEFITS]. Plus, you'll pick up an extra XXX points. Simply enter your device's Registration Code below.",
  },
  {
    name: "registerDeviceAccordionTitle",
    friendlyName: "Title for the accordion in the device registration dialog",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    defaultValue: "Where can I find my device's registration code?",
  },
  {
    name: "registerDeviceAccordionDescription",
    friendlyName: "Description for the accordion in the device registration dialog",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "richText",
    defaultValue:
      "You can find your Registration Code on the leaflet inside your device's box &lt;br/&gt; &lt;br/&gt; Can't find your leaflet?",
  },
  {
    name: "registerDeviceAccordionButtonText",
    friendlyName: "Button Text for the accordion in the device registration dialog for CTA",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    defaultValue: "Contact the Ploom Care Team for assistance",
  },
  {
    name: "registerDeviceAccordionButtonUrl",
    friendlyName: "Hyperlink for get more points CTA",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "url",
    defaultValue: "#",
  },
  {
    name: "confirmDeviceRegistrationButtonText",
    friendlyName: "Confirm Device Registration Button Text",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    defaultValue: "Register My Device",
  },
  {
    name: "isDeviceCodeFormatLabelVisible",
    friendlyName: "Show Device Code Format Label",
    type: "boolean",
    defaultValue: true,
  },
  {
    name: "cancelDeviceRegistrationButtonText",
    friendlyName: "Cancel Device Registration Button Text",
    showIf: `options.get("isDeviceRegistrationRequired") === true`,
    type: "string",
    defaultValue: "Not Now",
  },
] as const satisfies Inputs;

export const SmartSurveyPushBlockComponent: RegisteredComponent = {
  name: "SmartSurveyPushBlock",
  component: dynamic(async () =>
    withValidation(
      smartSurveyPushBlockInputs,
      (await import("./SmartSurveyPushBlock")).SmartSurveyPushBlock,
      "SmartSurveyPushBlock"
    )
  ),
  inputs: smartSurveyPushBlockInputs,
};
